import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Pencil, Search, Minus, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin full-field EDIT dialog for an existing order.
 * PATCH /api/admin/orders/:id/edit — every field is optional server-side; we
 * PATCH the entire form each save so admins can freely rewrite any part of
 * the order without knowing which fields changed.
 *
 * Design mirrors ManualInvoiceDialog (same tier/variant pricing preview so
 * the admin sees the same math the storefront does before submit).
 *
 * iter_133 — supports minimize: while `open` is true, if the parent hands us
 * a `draftRef` we hydrate initial state from it and write every user change
 * back to it. The draft key includes the order id so a Manual Order draft
 * can't accidentally hydrate an Edit dialog.
 */
const isSelectTier = (product) =>
  String(product?.tier || "").trim().toLowerCase() === "select";

export default function EditOrderDialog({ open, onOpenChange, order, onSaved, onMinimize, draftRef }) {
  const stash = () => (draftRef?.current && draftRef.current.orderId === order?.id) ? draftRef.current : null;

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [addr, setAddr] = useState({ label: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  const [lines, setLines] = useState([]); // {product, quantity, variant, variant_mg}
  const [productPool, setProductPool] = useState([]);
  const [productQuery, setProductQuery] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("manual");
  const [shippingOverride, setShippingOverride] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  // iter184 — Manual discount amount ($) override. Wins over discount code on
  // the backend. Empty string = fall back to code lookup. Gives admin full
  // control ("just take $9.45 off, don't second-guess me").
  const [discountAmount, setDiscountAmount] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [noteToBuyer, setNoteToBuyer] = useState("");
  const [status, setStatus] = useState("pending_payment");
  const [trackingCarrier, setTrackingCarrier] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [selectLadder, setSelectLadder] = useState({ 3: 0.05, 5: 0.10 });
  const [busy, setBusy] = useState(false);
  // iter184 — Opt-in buyer notification. Off by default so silent edits
  // (typo fixes, note updates) don't spam the customer. When checked, backend
  // fires an order-updated email including the admin's note.
  const [notifyBuyer, setNotifyBuyer] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState("");
  // iter185.5 Phase 2.1 — Fulfillment Mode explicit control.
  // Maps to (local_delivery, local_delivery_mode) server-side.
  const [fulfillmentMode, setFulfillmentMode] = useState("carrier");
  const [pickupNotes, setPickupNotes] = useState("");

  // Hydrate whenever an order is loaded into the dialog.
  useEffect(() => {
    if (!open || !order) return;
    const draft = stash();
    if (draft) {
      // Restore from minimized state — skip the full order re-hydrate.
      setCustomerName(draft.customerName || "");
      setCustomerEmail(draft.customerEmail || "");
      setAddr(draft.addr || { label: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
      setLines(draft.lines || []);
      setProductPool(draft.productPool || []);
      setPaymentMethod(draft.paymentMethod || "manual");
      setShippingOverride(draft.shippingOverride || "");
      setDiscountCode(draft.discountCode || "");
      setDiscountAmount(draft.discountAmount || "");
      setAdminNotes(draft.adminNotes || "");
      setNoteToBuyer(draft.noteToBuyer || "");
      setStatus(draft.status || "pending_payment");
      setTrackingCarrier(draft.trackingCarrier || "");
      setTrackingNumber(draft.trackingNumber || "");
      setSelectLadder(draft.selectLadder || { 3: 0.05, 5: 0.10 });
      setNotifyBuyer(!!draft.notifyBuyer);
      setNotifyMessage(draft.notifyMessage || "");
      setProductQuery("");
      return;
    }
    setCustomerName(order.customer_name || "");
    setCustomerEmail(order.customer_email || "");
    const a = order.address || {};
    setAddr({
      label: a.label || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      city: a.city || "",
      state: a.state || "",
      postal_code: a.postal_code || "",
      country: a.country || "US",
      ...(a.id ? { id: a.id } : {}),
    });
    setPaymentMethod(order.payment_method || "manual");
    setShippingOverride(order.shipping != null ? String(order.shipping) : "");
    setDiscountCode(order.discount_code || "");
    // Pre-fill the manual amount with the order's current discount so the live
    // preview equals the saved order out of the gate; admin can zero it out or
    // change it. If untouched + code exists, backend will still respect it.
    setDiscountAmount(order.discount != null && order.discount !== 0 ? String(order.discount) : "");
    setAdminNotes(order.admin_notes || "");
    setNoteToBuyer(order.note_to_buyer || "");
    setStatus(order.status || "pending_payment");
    setTrackingCarrier(order.tracking_carrier || "");
    setTrackingNumber(order.tracking_number || "");
    // iter185.5 Phase 2.1 — hydrate current fulfillment mode from stored fields.
    setFulfillmentMode(
      order.local_delivery
        ? (order.local_delivery_mode === "pickup" ? "local_pickup" : "local_delivery")
        : "carrier"
    );
    setPickupNotes(order.pickup_notes || "");
    // Every open starts with the notify checkbox OFF — must be explicit each time.
    setNotifyBuyer(false);
    setNotifyMessage("");
    setProductQuery("");

    // Fetch product pool + ladder in parallel so we can hydrate lines with
    // the FULL product object (needed for variant dropdowns / preview price).
    Promise.all([
      api.get("/products"),
      api.get("/select-ladder").catch(() => ({ data: { discount_3vial: 0.05, discount_5vial: 0.10 } })),
    ]).then(([{ data: pool }, { data: ladder }]) => {
      const arr = Array.isArray(pool) ? pool : [];
      setProductPool(arr);
      setSelectLadder({ 3: Number(ladder.discount_3vial || 0), 5: Number(ladder.discount_5vial || 0), 10: Number(ladder.discount_10vial || 0) });
      // Hydrate lines from order.items — join product_id → full product doc.
      const hydrated = (order.items || []).map((it) => {
        const prod = arr.find((p) => p.id === it.product_id) || {
          id: it.product_id,
          name: it.name,
          sku: "",
          price: it.price,
          tier: "supply",
          variants: [],
        };
        return {
          product: prod,
          quantity: Number(it.quantity || 1),
          variant: it.variant || (isSelectTier(prod) ? "1vial" : null),
          variant_mg: it.variant_mg || null,
          // iter184 — carry admin comp/custom price across edits
          price_override: it.price_override != null ? Number(it.price_override) : null,
        };
      });
      setLines(hydrated);
    }).catch(() => {
      setProductPool([]);
      setLines([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order]);

  // Sync every state change back to the parent's draftRef (keyed on order id).
  useEffect(() => {
    if (!draftRef || !order) return;
    draftRef.current = {
      orderId: order.id,
      customerName, customerEmail, addr, lines, productPool,
      paymentMethod, shippingOverride, discountCode, discountAmount, adminNotes,
      noteToBuyer, status, trackingCarrier, trackingNumber, selectLadder,
      notifyBuyer, notifyMessage,
    };
  }, [
    draftRef, order,
    customerName, customerEmail, addr, lines, productPool,
    paymentMethod, shippingOverride, discountCode, discountAmount, adminNotes,
    noteToBuyer, status, trackingCarrier, trackingNumber, selectLadder,
    notifyBuyer, notifyMessage,
  ]);

  const filtered = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return productPool
      .filter((p) => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.slug || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [productQuery, productPool]);

  const addLine = (product) => {
    let variant = null;
    let variant_mg = null;
    if (Array.isArray(product.variants) && product.variants.length > 0) {
      const avail = product.variants.find((v) => v && v.price != null && v.in_stock !== false)
        || product.variants.find((v) => v && v.price != null);
      if (avail) variant_mg = String(avail.mg);
    }
    if (isSelectTier(product)) variant = "1vial";
    setLines((prev) => [...prev, { product, quantity: 1, variant, variant_mg }]);
    setProductQuery("");
  };
  const setLineQty = (idx, n) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, quantity: Math.max(1, n) } : l));
  const setLineVariant = (idx, field, value) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const previewUnitPrice = (line) => {
    // iter184 — admin comp / custom price wins over the resolver preview.
    if (line.price_override != null && line.price_override !== "") {
      const n = Number(line.price_override);
      if (!Number.isNaN(n)) return +n.toFixed(2);
    }
    const p = line.product;
    if (isSelectTier(p)) {
      let unit = Number(p.price || 0);
      if (line.variant_mg && Array.isArray(p.variants)) {
        const m = p.variants.find((v) => String(v.mg) === String(line.variant_mg));
        if (m && m.price != null) unit = Number(m.price);
      }
      const n = line.variant === "3vial" ? 3 : line.variant === "5vial" ? 5 : line.variant === "10vial" ? 10 : 1;
      const disc = Number(selectLadder[n] || 0);
      return +(unit * n * (1 - disc)).toFixed(2);
    }
    if (line.variant_mg && Array.isArray(p.variants)) {
      const m = p.variants.find((v) => String(v.mg) === String(line.variant_mg));
      if (m && m.price != null) return Number(m.price);
    }
    return Number(p.price || 0);
  };
  const subtotalPreview = lines.reduce((sum, l) => sum + previewUnitPrice(l) * l.quantity, 0);
  // iter184 — Full live totals so the dialog shows EXACTLY what will save.
  const discountPreview = (() => {
    if (discountAmount !== "" && discountAmount !== null) {
      const n = Number(discountAmount);
      if (!Number.isNaN(n) && n >= 0) return Math.min(n, subtotalPreview);
    }
    return 0; // code-based discounts are computed server-side; preview treats as $0 until saved
  })();
  const shippingPreview = (() => {
    if (shippingOverride !== "" && shippingOverride !== null) {
      const n = Number(shippingOverride);
      if (!Number.isNaN(n) && n >= 0) return n;
    }
    return 0;
  })();
  const totalPreview = Math.max(0, subtotalPreview - discountPreview) + shippingPreview;
  const shippingIsAuto = shippingOverride === "" || shippingOverride === null;
  const setLinePriceOverride = (idx, val) => setLines((prev) => prev.map((l, i) => {
    if (i !== idx) return l;
    if (val === "" || val === null || val === undefined) return { ...l, price_override: null };
    const n = Number(val);
    if (Number.isNaN(n) || n < 0) return l;
    return { ...l, price_override: n };
  }));
  const compLine = (idx) => setLinePriceOverride(idx, 0);

  const submit = async () => {
    if (!customerName.trim()) { toast.error("Customer name is required"); return; }
    if (!customerEmail.trim()) { toast.error("Customer email is required"); return; }
    if (!addr.line1.trim() || !addr.city.trim() || !addr.state.trim() || !addr.postal_code.trim()) {
      toast.error("Shipping address is incomplete"); return;
    }
    if (lines.length === 0) { toast.error("Order must have at least one line item"); return; }

    const payload = {
      customer_name: customerName.trim(),
      customer_email: customerEmail.trim(),
      address: addr,
      items: lines.map((l) => ({
        product_id: l.product.id,
        name: l.product.name,
        price: previewUnitPrice(l),
        quantity: l.quantity,
        image_url: l.product.image_url,
        variant: l.variant,
        variant_mg: l.variant_mg,
        // iter184 — carry comp/custom price so the backend re-price honours it
        price_override: l.price_override != null && l.price_override !== "" ? Number(l.price_override) : null,
      })),
      payment_method: paymentMethod,
      shipping_override: shippingOverride === "" ? null : Number(shippingOverride),
      // iter184 — Manual amount wins server-side. When cleared, fall back to code.
      discount_amount: discountAmount === "" ? null : Number(discountAmount),
      discount_code: discountCode.trim() || null,  // send null when cleared so backend rebuilds discount
      admin_notes: adminNotes,
      note_to_buyer: noteToBuyer,
      status,
      tracking_carrier: trackingCarrier,
      tracking_number: trackingNumber,
      // iter185.5 Phase 2.1 — send fulfillment mode only when it actually
      // changed vs. the stored order shape (avoid needless writes/history).
      fulfillment_mode: (() => {
        const current = order.local_delivery
          ? (order.local_delivery_mode === "pickup" ? "local_pickup" : "local_delivery")
          : "carrier";
        return fulfillmentMode !== current ? fulfillmentMode : null;
      })(),
      pickup_notes: fulfillmentMode === "local_pickup" ? (pickupNotes || null) : null,
      // iter184 — opt-in buyer email
      notify_buyer: !!notifyBuyer,
      notify_message: notifyMessage.trim() || null,
    };

    // iter185.5 Phase 2.1 — pre-flight guard-rails BEFORE hitting the server:
    //  (a) Hard-block if switching AWAY from carrier while a live Shippo
    //      label exists — backend also blocks but early UX beats a round-trip.
    //  (b) Confirm when carrier tracking is attached (no label) — clarifies
    //      that historical tracking is preserved but no longer represents
    //      current fulfillment.
    if (payload.fulfillment_mode && payload.fulfillment_mode !== "carrier") {
      if (order.shippo_transaction_id) {
        toast.error("A shipping label has already been purchased for this order. Void the label before changing fulfillment mode.");
        return;
      }
      const hasTracking = !!(order.tracking_number || order.tracking_carrier);
      if (hasTracking) {
        const ok = window.confirm(
          "This order has carrier tracking information attached. Change fulfillment to " +
          (payload.fulfillment_mode === "local_pickup" ? "Local Pickup" : "Local Delivery") +
          "? Existing tracking information will be preserved in the order history but will no longer represent the current fulfillment method."
        );
        if (!ok) return;
      }
    }

    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/edit`, payload);
      toast.success(
        `Order ${data.order_number} updated — $${(data.total ?? 0).toFixed(2)}${notifyBuyer ? " · buyer emailed" : ""}`
      );
      onSaved?.(data);
      // Clear the stashed draft so the next edit starts fresh.
      if (draftRef) draftRef.current = null;
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e, "Order edit failed"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded-none border-borderSubtle bg-white focus:ring-0 focus:border-charcoal";

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl bg-bone border-borderSubtle max-h-[90vh] overflow-y-auto" data-testid="edit-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Pencil size={18} strokeWidth={2}/> Edit order {order.order_number}
          </DialogTitle>
        </DialogHeader>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            className="absolute right-12 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            data-testid="edt-minimize-btn"
            title="Minimize — keep this edit draft and come back later"
            aria-label="Minimize edit order"
          >
            <Minus className="h-4 w-4" strokeWidth={2}/>
          </button>
        )}
        <p className="text-xs text-charcoal/75 -mt-2 mb-4">
          Every field is editable. Prices are re-resolved server-side against the current catalog — line totals will match what a buyer would see.
        </p>

        {/* Buyer */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="overline">Customer name</Label>
            <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className={inputCls} data-testid="edt-customer-name"/>
          </div>
          <div>
            <Label className="overline">Customer email</Label>
            <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className={inputCls} data-testid="edt-customer-email"/>
          </div>
        </div>

        {/* Address */}
        <div className="mb-4">
          <Label className="overline mb-2 block">Shipping address</Label>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Line 1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className={inputCls} data-testid="edt-addr-line1"/>
            <Input placeholder="Line 2 (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} className={inputCls} data-testid="edt-addr-line2"/>
            <Input placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={inputCls} data-testid="edt-addr-city"/>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className={inputCls} data-testid="edt-addr-state"/>
              <Input placeholder="Postal code" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} className={inputCls} data-testid="edt-addr-zip"/>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="mb-4">
          <Label className="overline mb-2 block">Items</Label>
          <div className="relative mb-2">
            <div className="flex items-center gap-2">
              <Search size={14} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75"/>
              <Input
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                placeholder="Add another product by name, SKU, or slug…"
                className={`${inputCls} pl-9`}
                data-testid="edt-product-search"
              />
            </div>
            {filtered.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-borderSubtle max-h-56 overflow-y-auto shadow" data-testid="edt-product-suggestions">
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => addLine(p)}
                    className="w-full text-left px-3 py-2 hover:bg-bone/50 text-sm flex justify-between gap-3 border-b border-borderSubtle last:border-b-0"
                    data-testid={`edt-add-product-${p.slug}`}
                  >
                    <span className="truncate">
                      <span className="font-serif">{p.name}</span>
                      <span className="text-charcoal/75 font-mono text-[11px] ml-2 uppercase tracking-widest">{p.sku}</span>
                      {isSelectTier(p) && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] tracking-widest bg-charcoal text-bone rounded-sm">SELECT</span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-charcoal/75">${(p.price ?? 0).toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {lines.length === 0 ? (
            <p className="text-xs text-charcoal/75 italic" data-testid="edt-lines-empty">No products yet. Search above to add.</p>
          ) : (
            <div className="space-y-2" data-testid="edt-lines">
              {lines.map((l, idx) => {
                const p = l.product;
                const hasMgVariants = Array.isArray(p.variants) && p.variants.length > 0;
                const isSelect = isSelectTier(p);
                const laddered = isSelect && (l.variant === "3vial" || l.variant === "5vial" || l.variant === "10vial");
                return (
                  <div key={idx} className="grid grid-cols-[1fr_auto_72px_auto_auto] gap-3 items-end bg-white border border-borderSubtle rounded-xl p-3" data-testid={`edt-line-${idx}`}>
                    <div className="min-w-0">
                      <p className="font-serif text-sm truncate">{p.name}</p>
                      <p className="text-[11px] uppercase tracking-widest text-charcoal/75 font-mono">
                        {p.sku}
                        {isSelect && <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] tracking-widest bg-charcoal text-bone rounded-sm">SELECT</span>}
                      </p>
                    </div>
                    <div className="flex items-end gap-3">
                      {hasMgVariants && (
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Strength</span>
                          <select
                            value={l.variant_mg || ""}
                            onChange={(e) => setLineVariant(idx, "variant_mg", e.target.value)}
                            className={`${inputCls} text-xs h-9`}
                            data-testid={`edt-line-mg-${idx}`}
                          >
                            {p.variants.filter((v) => v && v.price != null).map((v) => (
                              <option key={String(v.mg)} value={String(v.mg)}>{v.mg}mg · ${Number(v.price).toFixed(2)}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isSelect && (
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Kit size</span>
                          <select
                            value={l.variant || "1vial"}
                            onChange={(e) => setLineVariant(idx, "variant", e.target.value)}
                            className={`${inputCls} text-xs h-9 min-w-[8.5rem] ${laddered ? "border-oxblood ring-1 ring-oxblood/30" : ""}`}
                            data-testid={`edt-line-variant-${idx}`}
                          >
                            <option value="1vial">1 Vial</option>
                            <option value="3vial">3 Vials — {Math.round((selectLadder[3]||0)*100)}% off</option>
                            <option value="5vial">5 Vials — {Math.round((selectLadder[5]||0)*100)}% off</option>
                            <option value="10vial">10 Vials — {Math.round((selectLadder[10]||0)*100)}% off</option>
                          </select>
                        </div>
                      )}
                      {!hasMgVariants && !isSelect && (
                        <span className="text-[11px] uppercase tracking-widest text-charcoal/75 font-mono">flat</span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Qty</span>
                      <Input
                        type="number"
                        min="1"
                        value={l.quantity}
                        onChange={(e) => setLineQty(idx, Number(e.target.value))}
                        className={`${inputCls} text-center h-9`}
                        data-testid={`edt-line-qty-${idx}`}
                      />
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Line total</span>
                      <span
                        className={`font-mono text-sm w-24 text-right h-9 flex items-center justify-end ${l.price_override != null ? "text-oxblood font-semibold" : ""}`}
                        data-testid={`edt-line-total-${idx}`}
                        title={l.price_override != null ? (Number(l.price_override) === 0 ? "Comped — this line is free" : "Custom price override") : ""}
                      >
                        {l.price_override != null && Number(l.price_override) === 0
                          ? "COMP"
                          : `$${(previewUnitPrice(l) * l.quantity).toFixed(2)}`}
                      </span>
                      {/* iter184 — Admin comp / custom price controls */}
                      <div className="flex items-center gap-1.5 mt-1">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="override $"
                          value={l.price_override != null ? String(l.price_override) : ""}
                          onChange={(e) => setLinePriceOverride(idx, e.target.value)}
                          className={`${inputCls} h-7 text-[11px] w-[92px] text-right`}
                          data-testid={`edt-line-override-${idx}`}
                          title="Set a custom unit price. Blank = normal price. 0 = free."
                        />
                        {l.price_override == null ? (
                          <button
                            onClick={() => compLine(idx)}
                            className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono border border-borderSubtle px-1.5 py-1 rounded-sm hover:border-oxblood hover:text-oxblood"
                            data-testid={`edt-line-comp-${idx}`}
                            title="Set unit price to $0.00 (free sample)"
                          >
                            Comp
                          </button>
                        ) : (
                          <button
                            onClick={() => setLinePriceOverride(idx, "")}
                            className="text-[10px] uppercase tracking-widest text-oxblood font-mono border border-oxblood px-1.5 py-1 rounded-sm hover:bg-oxblood hover:text-bone"
                            data-testid={`edt-line-clear-override-${idx}`}
                            title="Clear override and go back to normal price"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </div>
                    <button onClick={() => removeLine(idx)} className="text-charcoal/75 hover:text-oxblood p-1 self-center" data-testid={`edt-line-remove-${idx}`} aria-label="Remove">
                      <Trash2 size={14} strokeWidth={1.5}/>
                    </button>
                  </div>
                );
              })}
              <div className="flex justify-end text-sm mt-2">
                <span className="text-charcoal/75 mr-3">Subtotal preview:</span>
                <span className="font-mono font-semibold" data-testid="edt-subtotal-preview">${subtotalPreview.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Order controls */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="overline">Payment method</Label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={`${inputCls} w-full`} data-testid="edt-payment-method">
              <option value="manual">Manual (cash/wire/other)</option>
              <option value="venmo">Venmo</option>
              <option value="cashapp">Cash App</option>
              <option value="stripe">Stripe</option>
            </select>
          </div>
          <div>
            <Label className="overline">Shipping override</Label>
            <Input type="number" step="0.01" min="0" placeholder="Auto if blank" value={shippingOverride} onChange={(e) => setShippingOverride(e.target.value)} className={inputCls} data-testid="edt-shipping-override"/>
          </div>
          <div>
            <Label className="overline">Discount $ (manual)</Label>
            <Input type="number" step="0.01" min="0" placeholder="Blank = use code" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} className={inputCls} data-testid="edt-discount-amount" title="Manual dollar discount. Wins over the code below."/>
          </div>
          <div>
            <Label className="overline">Discount code</Label>
            <Input placeholder="Blank to clear" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} className={inputCls} data-testid="edt-discount-code" title="For record-keeping / packing slip label. Ignored when Discount $ has a value."/>
          </div>
        </div>

        {/* iter184 — Live total breakdown. Whatever you see is what will save. */}
        <div className="border border-oxblood/25 bg-oxblood/[0.03] rounded-lg p-4 mb-4" data-testid="edt-live-total-panel">
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Subtotal</p>
              <p className="font-mono font-semibold" data-testid="edt-preview-subtotal">${subtotalPreview.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Discount</p>
              <p className={`font-mono ${discountPreview > 0 ? "text-oxblood font-semibold" : "text-charcoal/60"}`} data-testid="edt-preview-discount">
                {discountPreview > 0 ? `−$${discountPreview.toFixed(2)}` : "$0.00"}
                {discountAmount === "" && discountCode.trim() && <span className="ml-1 text-[10px] text-charcoal/60">(auto on save)</span>}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Shipping</p>
              <p className="font-mono" data-testid="edt-preview-shipping">
                {shippingIsAuto ? <span className="text-charcoal/60">auto on save</span> : `$${shippingPreview.toFixed(2)}`}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Total</p>
              <p className="font-mono text-lg font-bold text-oxblood" data-testid="edt-preview-total">
                ${totalPreview.toFixed(2)}
                {shippingIsAuto && <span className="ml-1 text-[10px] text-charcoal/60 font-normal">+ ship</span>}
              </p>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-charcoal/60 leading-snug">
            Discount $ overrides the code. Shipping override wins over auto. Set both explicitly for a total that matches this preview exactly.
          </p>
        </div>

        {/* iter185.5 Phase 2.1 — Fulfillment Mode explicit control */}
        <div className="mb-4">
          <Label className="overline">Fulfillment mode</Label>
          <div className="mt-1.5 inline-flex rounded-full border border-borderSubtle bg-white p-1" data-testid="edt-fulfillment-mode">
            {[
              { key: "carrier",        label: "Carrier Shipping" },
              { key: "local_delivery", label: "Local Delivery" },
              { key: "local_pickup",   label: "Local Pickup" },
            ].map((opt) => {
              const active = fulfillmentMode === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFulfillmentMode(opt.key)}
                  className={`px-3.5 py-1.5 text-[12px] uppercase tracking-[0.18em] rounded-full transition-colors ${
                    active ? "bg-oxblood text-bone" : "text-charcoal/75 hover:text-charcoal"
                  }`}
                  data-testid={`edt-fulfillment-${opt.key}`}
                >{opt.label}</button>
              );
            })}
          </div>
          {fulfillmentMode !== "carrier" && order.shippo_transaction_id && (
            <p className="mt-2 text-[12px] text-oxblood" data-testid="edt-fulfillment-label-warn">
              A Shippo shipping label is attached — void it first via the Fulfillment panel before switching to Local.
            </p>
          )}
          {fulfillmentMode === "local_pickup" && (
            <div className="mt-3">
              <Label className="overline">Pickup notes (optional, internal + on slip)</Label>
              <Input value={pickupNotes} onChange={(e) => setPickupNotes(e.target.value)} className={inputCls} placeholder="e.g. front door · 4pm Fri · call on arrival" data-testid="edt-pickup-notes"/>
            </div>
          )}
          <p className="mt-2 text-[11px] text-charcoal/60 leading-snug">
            Shipping price is independent from mode — set the price separately below via Shipping override.
          </p>
        </div>

        {/* Status + tracking */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <Label className="overline">Status</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-full`} data-testid="edt-status">
              <option value="pending_payment">Pending payment</option>
              <option value="paid">Paid</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <Label className="overline">Tracking carrier</Label>
            <select value={trackingCarrier} onChange={(e) => setTrackingCarrier(e.target.value)} className={`${inputCls} w-full`} data-testid="edt-tracking-carrier">
              <option value="">— None —</option>
              <option value="USPS">USPS</option>
              <option value="UPS">UPS</option>
              <option value="FedEx">FedEx</option>
              <option value="DHL">DHL</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <Label className="overline">Tracking number</Label>
            <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className={inputCls} data-testid="edt-tracking-number"/>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="overline">Admin note (internal)</Label>
            <Input value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className={inputCls} data-testid="edt-admin-notes"/>
          </div>
          <div>
            <Label className="overline">Note to buyer (public)</Label>
            <Input value={noteToBuyer} onChange={(e) => setNoteToBuyer(e.target.value)} className={inputCls} data-testid="edt-note-to-buyer"/>
          </div>
        </div>

        {/* iter184 — Opt-in buyer email. Off by default. When checked, backend
             fires an "order updated" email including the note below. */}
        <div className={`border rounded-lg p-4 mb-4 transition-colors ${notifyBuyer ? "border-oxblood bg-oxblood/[0.04]" : "border-borderSubtle bg-white"}`} data-testid="edt-notify-panel">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={notifyBuyer}
              onChange={(e) => setNotifyBuyer(e.target.checked)}
              className="mt-1 accent-oxblood h-4 w-4"
              data-testid="edt-notify-buyer-checkbox"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Mail size={14} strokeWidth={2} className={notifyBuyer ? "text-oxblood" : "text-charcoal/60"}/>
                <span className={`text-[13px] font-semibold uppercase tracking-[0.18em] ${notifyBuyer ? "text-oxblood" : "text-charcoal"}`}>
                  Email buyer a summary of this update
                </span>
              </div>
              <p className="text-[12px] text-charcoal/70 mt-1 leading-snug">
                Sends the buyer an "order updated" email with the new totals and item list. Leave unchecked for silent edits (typo fixes, comping a sample without telling them, etc.).
              </p>
            </div>
          </label>
          {notifyBuyer && (
            <div className="mt-3 pl-7">
              <Label className="overline text-oxblood">Message to include in the email (optional)</Label>
              <textarea
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Hi Greg — added a free vial of BPC-157 on us as a thank-you. Shipping today. Reply here if anything looks off."
                rows={3}
                className={`${inputCls} w-full resize-y min-h-[72px]`}
                data-testid="edt-notify-message"
              />
              <p className="text-[11px] text-charcoal/60 mt-1">
                Appears as a highlighted note at the top of the email, above the updated order summary.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 border border-borderSubtle text-charcoal tracking-[0.2em] text-xs uppercase hover:border-charcoal transition-colors"
            data-testid="edt-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-5 py-2 bg-oxblood text-bone tracking-[0.2em] text-xs uppercase hover:bg-charcoal transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="edt-save"
          >
            <Plus size={12} strokeWidth={2}/> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
